import React, { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { useHistory, useLocation } from "react-router-dom";
import { useQuery } from "react-query";

import Loader from "../../Components/Loader/Loader";
import { fetchActivities } from "../../Redux/Actions/Activity";
import { fetchOffices } from "../../Redux/Actions/Offices";
import { handleResponse } from "../../utils/Notification";
import { formatDate } from "../../utils/common";

import "react-datepicker/dist/react-datepicker.css";
import "./activity.scss";

const parseDate = (value) => {
  if (!value) return new Date();

  const match = value.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);

  if (match) {
    const parsed = new Date(
      Number(match[1]),
      Number(match[2]) - 1,
      Number(match[3])
    );

    if (
      parsed.getFullYear() === Number(match[1]) &&
      parsed.getMonth() === Number(match[2]) - 1 &&
      parsed.getDate() === Number(match[3])
    ) {
      return parsed;
    }

    return new Date();
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? new Date() : parsed;
};

const formatWorkOrder = (value) => {
  if (!value) return "N/A";
  return String(value).split("-").pop();
};

export default function Activity() {
  const history = useHistory();
  const location = useLocation();

  const searchParams = new URLSearchParams(location.search);
  const appliedOffice = searchParams.get("selected_office") || "";
  const appliedDate = searchParams.get("selected_date") || "";

  const [selectedOffice, setSelectedOffice] = useState(appliedOffice);
  const [date, setDate] = useState(() => parseDate(appliedDate));

  // Sync filter selections when the applied URL changes, including
  // Search submissions and browser Back/Forward navigation.
  useEffect(() => {
    setSelectedOffice(appliedOffice);
    setDate(parseDate(appliedDate));
  }, [appliedOffice, appliedDate]);

  const {
    data: offices,
    isError: officesError
  } = useQuery("offices", fetchOffices, {
    initialData: [],
    refetchOnWindowFocus: false
  });

  const {
    data: activities,
    isFetching,
    isError,
    refetch
  } = useQuery(
    ["activities", appliedOffice, appliedDate],
    () => {
      const params = {};

      if (appliedOffice) params.office_id = appliedOffice;
      if (appliedDate) params.date = appliedDate;

      return fetchActivities(params);
    },
    {
      refetchOnWindowFocus: false,
      onError: handleResponse
    }
  );

  const applyFilters = (event) => {
    event.preventDefault();

    const params = new URLSearchParams();
    params.set("selected_office", selectedOffice);
    params.set("selected_date", formatDate(date || new Date()));

    history.push({
      pathname: "/",
      search: `?${params.toString()}`
    });
  };

  const openActivity = (activity) => {
    // Preserve the filters used to fetch these results,
    // not selections the employee has not applied yet.
    const params = new URLSearchParams();
    if (appliedOffice) params.set("selected_office", appliedOffice);
    if (appliedDate) params.set("selected_date", appliedDate);

    if (activity.activity_id != null) {
      params.set("activity_id", activity.activity_id);
    }

    history.push({
      pathname: `/activity/${activity.id}`,
      search: `?${params.toString()}`
    });
  };

  const activityRecords = Array.isArray(activities?.data)
    ? activities.data.filter(
        (activity) => activity.activity_type !== "Storage"
      )
    : [];


  return (
    <main className="ds-activity">
      <div className="ds-activity__heading">
        <h1>Activity</h1>
      </div>

      <form className="ds-activity__filters" onSubmit={applyFilters}>
        <div className="ds-activity__field">
          <label htmlFor="activity-date">Date</label>
          <DatePicker
            id="activity-date"
            selected={date}
            onChange={setDate}
            dateFormat="MM/dd/yyyy"
            required
          />
        </div>

        <div className="ds-activity__field">
          <label htmlFor="activity-office">Office</label>
          <select
            id="activity-office"
            value={selectedOffice}
            onChange={(event) => setSelectedOffice(event.target.value)}
          >
            <option value="">All Offices</option>

            {(offices?.data || []).map((office) => (
              <option key={office.id} value={office.id}>
                {office.name}
              </option>
            ))}
          </select>
        </div>

        <button type="submit" className="ds-activity__search">
          Search Activities
        </button>

        {officesError && (
          <p className="ds-activity__notice" role="alert">
            Office options could not be loaded.
          </p>
        )}
      </form>

      <div className="ds-activity__results-heading">
        <h2>Work Orders</h2>
        {!isFetching && !isError && (
          <span>{activityRecords.length} activities</span>
        )}
      </div>

      {isFetching && <Loader />}

      {isError && (
        <div className="ds-activity__empty" role="alert">
          <p>Unable to load activities.</p>
          <button type="button" onClick={() => refetch()}>
            Try Again
          </button>
        </div>
      )}

      {!isError && !isFetching && activityRecords.length === 0 && (
        <div className="ds-activity__empty">
          No activities found for the selected filters.
        </div>
      )}

      {!isError && (
        <div className="ds-activity__list">
          {activityRecords.map((activity) => {
            const order = formatWorkOrder(
              activity.work_order_number
            );

            const activityType = [
              activity.activity_type,
              activity.detail_type
            ].filter(Boolean).join(" | ");

            return (
              <button
                type="button"
                key={activity.id}
                className="ds-activity__card"
                onClick={() => openActivity(activity)}
                aria-label={`Open work order ${order} for ${
                  activity.vehicle_model_name || "vehicle"
                }`}
              >
                <div className="ds-activity__card-top">
                  <h3>
                    {activity.vehicle_model_name || "Vehicle"}
                  </h3>
                  <span className="ds-activity__chevron" aria-hidden="true">
                    ›
                  </span>
                </div>

                <div className="ds-activity__type-line">
                  <span className="ds-activity__type">
                    {activityType || "Activity"}
                  </span>
                  {activity.alternate_to && String(activity.alternate_to).trim() && (
                    <span className="ds-activity__contact">
                      {" - "}{activity.alternate_to}
                    </span>
                  )}
                </div>

                <div className="ds-activity__card-footer">
                  <span className="ds-activity__order">WO: {order}</span>
                  <span className="ds-activity__plate">
                    P: {activity.plate || "—"}
                  </span>
                  <span className="ds-activity__vin">V: {activity.vin || "N/A"}</span>
                </div>

              </button>
            );
          })}
        </div>
      )}
    </main>
  );
}
